import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FaUserEdit, FaCamera, FaKey, FaSave, FaEnvelope, FaShieldAlt, FaMapMarkerAlt, FaPhoneAlt, FaUser } from 'react-icons/fa';
import { showSuccess, showError, showLoading, closeLoading } from '../../../utils/Alert';
import Loader from '../../shared/Loader';
import { API_URL } from '../../../config';
import './RecruiterProfile.css';

const RecruiterProfile = () => {
    const [loading, setLoading] = useState(true);
    const [profile, setProfile] = useState({
        name: '',
        email: '',
        phone: '',
        role: 'RECRUITER',
        location: '',
        bio: '',
        profileImage: ''
    });

    const fileInputRef = React.useRef(null);

    const [passwords, setPasswords] = useState({
        current: '',
        newPass: '',
        confirmPass: ''
    });

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const token = localStorage.getItem('token');
                if (!token) return;
                const res = await axios.get('/api/users/me', {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setProfile({
                    name: res.data.name || '',
                    email: res.data.email || '',
                    phone: res.data.phone || '',
                    role: res.data.role?.toUpperCase() || 'RECRUITER',
                    location: res.data.location || '',
                    bio: res.data.bio || '',
                    profileImage: res.data.profileImage || ''
                });
                setLoading(false);
            } catch (err) {
                console.error("Error fetching profile", err);
                setLoading(false);
            }
        };
        fetchProfile();
    }, []);

    const handleProfileChange = (e) => {
        setProfile({ ...profile, [e.target.name]: e.target.value });
    };

    const handlePasswordChange = (e) => {
        setPasswords({ ...passwords, [e.target.name]: e.target.value });
    };

    const handleAvatarClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('image', file);

        showLoading('Uploading Avatar...');
        try {
            const token = localStorage.getItem('token');
            const res = await axios.post('/api/users/profile-image', formData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data'
                }
            });
            setProfile(prev => ({ ...prev, profileImage: res.data.profileImage }));
            closeLoading();
            showSuccess('Upload Success', 'Your profile picture has been updated.');
            window.dispatchEvent(new Event('profileUpdated'));
        } catch (err) {
            console.error('Error uploading image', err);
            closeLoading();
            showError('Upload Failed', 'Could not save profile image.');
        }
    };

    const saveProfile = async (e) => {
        e.preventDefault();
        showLoading('Updating profile...');
        try {
            const token = localStorage.getItem('token');
            await axios.put('/api/users/profile', profile, {
                headers: { Authorization: `Bearer ${token}` }
            });
            closeLoading();
            showSuccess('Profile Updated', 'Your profile details have been saved.');
            window.dispatchEvent(new Event('profileUpdated'));
        } catch (err) {
            console.error(err);
            closeLoading();
            showError('Update Failed', 'There was an error updating your profile.');
        }
    };

    const savePassword = async (e) => {
        e.preventDefault();
        if (passwords.newPass !== passwords.confirmPass) {
            return showError('Validation Error', 'New passwords do not match.');
        }
        showLoading('Changing password...');
        try {
            const token = localStorage.getItem('token');
            await axios.post('/api/auth/change-password', {
                currentPassword: passwords.current,
                newPassword: passwords.newPass
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            closeLoading();
            showSuccess('Security Updated', 'Your password has been changed successfully.');
            setPasswords({ current: '', newPass: '', confirmPass: '' });
        } catch (err) {
            console.error(err);
            closeLoading();
            showError('Update Failed', err.response?.data?.message || 'Failed to update password.');
        }
    };

    if (loading) return <Loader />;

    return (
        <div className="profile-section-wrapper fade-in">
            <div className="dashboard-header">
                <h1 className="page-title">Personal Identity & Settings</h1>
                <p className="page-subtitle">Configure your recruiter profile and account security preferences.</p>
            </div>

            <div className="profile-layout-grid">
                {/* Profile Card */}
                <div className="profile-card avatar-card">
                    <div className="profile-avatar-container">
                        <div className="avatar-preview-wrapper">
                            {profile.profileImage ? (
                                <img src={`${API_URL}${profile.profileImage}`} alt="Profile" className="avatar-img" />
                            ) : (
                                <div className="avatar-placeholder">
                                    {profile.name ? profile.name.charAt(0).toUpperCase() : 'R'}
                                </div>
                            )}
                            <button className="avatar-edit-btn" onClick={handleAvatarClick} title="Change Profile Picture">
                                <FaCamera />
                            </button>
                            <input
                                type="file"
                                accept="image/*"
                                style={{ display: 'none' }}
                                ref={fileInputRef}
                                onChange={handleImageUpload}
                            />
                        </div>
                        <h2 className="user-name">{profile.name}</h2>
                        <span className="user-role-badge">Institutional Recruiter</span>
                    </div>

                    <div className="profile-info-list">
                        <div className="info-item">
                            <FaEnvelope className="info-icon" />
                            <span>{profile.email}</span>
                        </div>
                        <div className="info-item">
                            <FaMapMarkerAlt className="info-icon" />
                            <span>{profile.location || 'Location not set'}</span>
                        </div>
                        <div className="info-item">
                            <FaShieldAlt className="info-icon" />
                            <span>Verified Access Member</span>
                        </div>
                    </div>
                </div>

                {/* Profile Details */}
                <div className="premium-card profile-settings-card">
                    <h3 className="section-title"><FaUserEdit /> Public Profile Details</h3>
                    <form onSubmit={saveProfile} className="settings-form">
                        <div className="form-group">
                            <label>Full Name</label>
                            <input
                                type="text" name="name" value={profile.name} onChange={handleProfileChange}
                                placeholder="Enter your full name" required
                            />
                        </div>
                        <div className="form-row">
                            <div className="form-group">
                                <label>Email Address (Immutable)</label>
                                <input
                                    type="email" value={profile.email} readOnly
                                    className="readonly-input"
                                />
                            </div>
                            <div className="form-group">
                                <label>Phone Number</label>
                                <input
                                    type="text" name="phone" value={profile.phone} onChange={handleProfileChange}
                                    placeholder="+X XXX XXX XXXX"
                                />
                            </div>
                        </div>
                        <div className="form-group">
                            <label>Location</label>
                            <input
                                type="text" name="location" value={profile.location} onChange={handleProfileChange}
                                placeholder="City, Country"
                            />
                        </div>
                        <div className="form-group">
                            <label>Professional Bio</label>
                            <textarea
                                name="bio" value={profile.bio} onChange={handleProfileChange}
                                placeholder="Write a brief professional summary..."
                                rows="3"
                            ></textarea>
                        </div>
                        <div className="form-footer">
                            <button type="submit" className="primary-btn pulse-on-hover">
                                <FaSave /> Save Profile Changes
                            </button>
                        </div>
                    </form>
                </div>

                {/* Security Card - Spans full width */}
                <div className="premium-card profile-security-card">
                    <h3 className="section-title"><FaKey /> Security & Authentication</h3>
                    <form onSubmit={savePassword} className="settings-form">
                        <div className="form-row triplet">
                            <div className="form-group">
                                <label>Current Password</label>
                                <input
                                    type="password" name="current" value={passwords.current} onChange={handlePasswordChange}
                                    placeholder="••••••••" required
                                />
                            </div>
                            <div className="form-group">
                                <label>New Password</label>
                                <input
                                    type="password" name="newPass" value={passwords.newPass} onChange={handlePasswordChange}
                                    placeholder="Min. 8 characters" required
                                />
                            </div>
                            <div className="form-group">
                                <label>Confirm Password</label>
                                <input
                                    type="password" name="confirmPass" value={passwords.confirmPass} onChange={handlePasswordChange}
                                    placeholder="Re-type new password" required
                                />
                            </div>
                        </div>
                        <div className="form-footer">
                            <button type="submit" className="primary-btn pulse-on-hover">
                                Update Security Settings
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default RecruiterProfile;
